/**
 * Chart utility functions for Research modal StockChart
 * Extracted from TechnicalAnalysis/CandlestickChart.jsx for reuse
 */

// Strict validation for numeric values
export const isValidNumber = (val) => {
  if (val === null || val === undefined || val === '') return false;
  const num = typeof val === 'number' ? val : parseFloat(val);
  return Number.isFinite(num) && num > 0;
};

/**
 * Convert various time formats to Unix timestamp in SECONDS.
 * Always returns a number — avoids mixed time types (string vs number)
 * which cause "time must be of type BusinessDay" errors in lightweight-charts.
 */
export const formatTime = (dateValue) => {
  if (!dateValue) return null;

  if (typeof dateValue === 'number' && dateValue < 9999999999) {
    return dateValue;
  }
  if (typeof dateValue === 'number' && dateValue > 9999999999) {
    return Math.floor(dateValue / 1000);
  }

  if (typeof dateValue === 'string') {
    // For date-only strings like '2026-02-13', append T00:00:00 for consistent parsing
    const str = dateValue.includes('T') || dateValue.includes(':')
      ? dateValue
      : `${dateValue.split('T')[0]}T00:00:00`;
    const timestamp = new Date(str).getTime();
    if (isNaN(timestamp)) return null;
    return Math.floor(timestamp / 1000);
  }

  return null;
};

// Validate raw OHLCV candle before transformation
export const isValidRawCandle = (candle) => {
  if (!candle) return false;
  const hasDate = candle.date || candle.datetime || candle.timestamp;
  if (!hasDate) return false;
  return (
    isValidNumber(candle.open) &&
    isValidNumber(candle.high) &&
    isValidNumber(candle.low) &&
    isValidNumber(candle.close)
  );
};

// Validate formatted candle (after transformation for lightweight-charts)
export const isValidFormattedCandle = (candle) => {
  if (!candle || !candle.time) return false;
  const validTime = typeof candle.time === 'number'
    ? Number.isFinite(candle.time) && candle.time > 0
    : typeof candle.time === 'string' && candle.time.length > 0;
  return validTime &&
    Number.isFinite(candle.open) && candle.open > 0 &&
    Number.isFinite(candle.high) && candle.high > 0 &&
    Number.isFinite(candle.low) && candle.low > 0 &&
    Number.isFinite(candle.close) && candle.close > 0;
};

/**
 * Remove consecutive duplicate timestamps from sorted data.
 * lightweight-charts crashes on duplicate timestamps.
 */
export const deduplicateByTime = (sortedData) => {
  if (!sortedData || sortedData.length <= 1) return sortedData;
  const result = [sortedData[0]];
  for (let i = 1; i < sortedData.length; i++) {
    if (sortedData[i].time !== sortedData[i - 1].time) {
      result.push(sortedData[i]);
    }
  }
  return result;
};

/**
 * Aggregate weekly OHLCV candles into monthly candles.
 * Groups by YYYY-MM from each candle's date, taking:
 * - open: first candle's open
 * - high: max of all highs
 * - low: min of all lows
 * - close: last candle's close
 * - volume: sum of all volumes
 *
 * @param {Array} weeklyData - Weekly candles (oldest-first)
 * @returns {Array} Monthly candles (oldest-first)
 */
export const aggregateToMonthly = (weeklyData) => {
  if (!weeklyData || weeklyData.length === 0) return [];

  const months = {};
  const monthOrder = [];

  weeklyData.forEach(candle => {
    const dateStr = candle.date || candle.datetime || '';
    const ym = dateStr.substring(0, 7); // 'YYYY-MM'
    if (!ym || ym.length < 7) return;

    if (!months[ym]) {
      months[ym] = {
        date: `${ym}-01`, // First of month
        open: Number(candle.open),
        high: Number(candle.high),
        low: Number(candle.low),
        close: Number(candle.close),
        volume: Number(candle.volume) || 0,
      };
      monthOrder.push(ym);
    } else {
      const m = months[ym];
      m.high = Math.max(m.high, Number(candle.high));
      m.low = Math.min(m.low, Number(candle.low));
      m.close = Number(candle.close);
      m.volume += Number(candle.volume) || 0;
    }
  });

  return monthOrder.map(ym => months[ym]);
};

/**
 * Transform raw OHLCV data (from API, newest-first) into
 * chart-ready format (oldest-first, validated, deduplicated).
 *
 * @param {Array} rawData - OHLCV candles from fetchHistoricalOHLCV (newest-first)
 * @returns {Array} Chart-ready candles (oldest-first) with { time, open, high, low, close, volume }
 */
export const prepareChartData = (rawData) => {
  if (!rawData || rawData.length === 0) return [];

  const isIntraday = (() => {
    const sample = rawData[0];
    const d = sample?.date || sample?.datetime || '';
    return typeof d === 'string' && (d.includes('T') || d.includes(':'));
  })();

  // Filter valid candles
  const valid = rawData.filter(isValidRawCandle);

  // Transform to lightweight-charts format
  const formatted = valid.map(candle => {
    const rawTime = candle.date || candle.datetime || candle.timestamp;
    const time = formatTime(rawTime, isIntraday);
    if (!time) return null;
    const adjClose = Number(candle.adjusted_close) || Number(candle.close);
    const rawClose = Number(candle.close);
    const factor = rawClose > 0 ? adjClose / rawClose : 1;
    return {
      time,
      open: Number(candle.open) * factor,
      high: Number(candle.high) * factor,
      low: Number(candle.low) * factor,
      close: adjClose,
      volume: Number(candle.volume) || 0,
    };
  }).filter(c => c !== null && isValidFormattedCandle(c));

  // Sort oldest-first (ascending time)
  formatted.sort((a, b) => {
    const tA = typeof a.time === 'number' ? a.time : new Date(a.time).getTime() / 1000;
    const tB = typeof b.time === 'number' ? b.time : new Date(b.time).getTime() / 1000;
    return tA - tB;
  });

  return deduplicateByTime(formatted);
};

/**
 * Calculate 7 bomb-level price lines from a baseline price and threshold percentage.
 * Used by StockChart in "bomb" timeframe view to show BaggerBomb scoring zones.
 *
 * @param {number} baselinePrice - The locked/baseline price for the asset
 * @param {number} threshold - The threshold percentage (e.g. 2.5 for 2.5%)
 * @returns {Array} 7 level objects with { price, label, tier, color, lineWidth, lineStyle, points }
 */
export function calculateBombLevels(baselinePrice, threshold) {
  if (!baselinePrice || !threshold || baselinePrice <= 0 || threshold <= 0) return [];
  const pct = threshold / 100;
  return [
    { price: baselinePrice * (1 + pct * 2.0), label: '\uD83D\uDD25 +50 pts', tier: 'tenBagger',    color: '#ffd700', lineWidth: 1.5, lineStyle: 0, points: 50 },
    { price: baselinePrice * (1 + pct * 1.5), label: '\uD83D\uDCA3\uD83D\uDCA3 +30 pts', tier: 'doubleBagger', color: '#ff9500', lineWidth: 1.5, lineStyle: 0, points: 30 },
    { price: baselinePrice * (1 + pct * 1.0), label: '\uD83D\uDCA3 +15 pts', tier: 'bagger',        color: '#00ff88', lineWidth: 2,   lineStyle: 0, points: 15 },
    { price: baselinePrice,                    label: '\u2014 Baseline \u2014',   tier: 'baseline',      color: 'rgba(255,255,255,0.4)', lineWidth: 1, lineStyle: 1, points: 0 },
    { price: baselinePrice * (1 - pct * 1.0), label: '\uD83D\uDCC9 Bust -10 pts',    tier: 'bust',           color: '#ef4444', lineWidth: 2, lineStyle: 0, points: -10 },
    { price: baselinePrice * (1 - pct * 1.5), label: '\u26A0\uFE0F Crash -20 pts',    tier: 'crash',          color: '#f97316', lineWidth: 2, lineStyle: 0, points: -20 },
    { price: baselinePrice * (1 - pct * 2.0), label: '\u2620\uFE0F Meltdown -35 pts',    tier: 'meltdown',       color: '#fbbf24', lineWidth: 2, lineStyle: 0, points: -35 },
  ];
}

/**
 * BaggerBomb stock thresholds v2, Ruling 15: a measured-model position's levels.
 * The six prices are the committed reward context's exact targets from the
 * fill \u2014 never re-derived from a threshold (BUILD_RULES \u00A79) \u2014 styled per tier
 * like calculateBombLevels, with the fill as the "Entry" line. A target at or
 * below $0 is unreachable and is not drawn.
 *
 * @param {number} fill - The position's receipt fill
 * @param {Array<{key: string, price: number}>} targets - The context's targets
 */
export function measuredBombLevels(fill, targets) {
  if (!(typeof fill === 'number' && fill > 0) || !Array.isArray(targets)) return [];
  const style = Object.fromEntries(calculateBombLevels(1, 1).map(level => [level.tier, level]));
  const levels = targets
    .filter(t => style[t?.key] && t.key !== 'baseline' && typeof t.price === 'number' && Number.isFinite(t.price) && t.price > 0)
    .map(t => ({ ...style[t.key], price: t.price }));
  levels.push({ ...style.baseline, price: fill, label: '\u2014 Entry \u2014' });
  return levels.sort((a, b) => b.price - a.price);
}

/**
 * Detect candles where price crossed a bomb level threshold.
 * Scans chart-ready data (oldest-first) for crossings.
 *
 * @param {Array} chartData - Oldest-first candles with { time, open, high, low, close }
 * @param {Array} bombLevels - From calculateBombLevels()
 * @returns {Array} Marker objects for lightweight-charts setMarkers()
 */
export function detectBombCrossings(chartData, bombLevels) {
  if (!chartData || chartData.length < 2 || !bombLevels || bombLevels.length === 0) return [];

  const markers = [];

  for (let i = 1; i < chartData.length; i++) {
    const prev = chartData[i - 1];
    const curr = chartData[i];

    bombLevels.forEach(level => {
      if (level.tier === 'baseline') return; // Skip baseline crossings

      // Upward crossing: previous close below level, current high reached level
      const crossedUp = prev.close < level.price && curr.high >= level.price;
      // Downward crossing: previous close above level, current low reached level
      const crossedDown = prev.close > level.price && curr.low <= level.price;

      if (crossedUp) {
        markers.push({
          time: curr.time,
          position: 'belowBar',
          color: level.color,
          shape: 'arrowUp',
          text: `${level.points > 0 ? '+' : ''}${level.points}`,
        });
      } else if (crossedDown) {
        markers.push({
          time: curr.time,
          position: 'aboveBar',
          color: level.color,
          shape: 'arrowDown',
          text: `${level.points}`,
        });
      }
    });
  }

  // lightweight-charts requires markers sorted by time ascending
  markers.sort((a, b) => {
    const tA = typeof a.time === 'number' ? a.time : 0;
    const tB = typeof b.time === 'number' ? b.time : 0;
    return tA - tB;
  });

  return markers;
}

/**
 * Calculate the nearest bomb level above and below the current price.
 *
 * @param {number} currentPrice - Latest price
 * @param {Array} bombLevels - From calculateBombLevels()
 * @param {Array} [triggeredTiers=[]] - Tier names already triggered (e.g., ['bagger', 'bust'])
 * @returns {{ above: { tier, price, points, distance, pctAway } | null, below: { tier, price, points, distance, pctAway } | null }}
 */
export function calculateNearestLevel(currentPrice, bombLevels, triggeredTiers = []) {
  if (!currentPrice || !bombLevels || bombLevels.length === 0) {
    return { above: null, below: null };
  }

  let nearestAbove = null;
  let nearestBelow = null;

  bombLevels.forEach(level => {
    // Skip baseline — it's a reference point, not an actionable level
    if (level.tier === 'baseline') return;

    // Skip already-triggered levels — advance to next uncrossed target
    if (triggeredTiers.length > 0 && triggeredTiers.includes(level.tier)) return;

    const distance = level.price - currentPrice;
    const pctAway = (distance / currentPrice) * 100;

    if (distance > 0) {
      // Level is above current price
      if (!nearestAbove || distance < nearestAbove.distance) {
        nearestAbove = { tier: level.tier, price: level.price, points: level.points, color: level.color, distance, pctAway };
      }
    } else if (distance < 0) {
      // Level is below current price
      if (!nearestBelow || Math.abs(distance) < Math.abs(nearestBelow.distance)) {
        nearestBelow = { tier: level.tier, price: level.price, points: level.points, color: level.color, distance, pctAway };
      }
    }
  });

  return { above: nearestAbove, below: nearestBelow };
}
